import React from 'react';
import PropTypes from 'prop-types';
import { Redirect, withRouter } from 'react-router-dom';
import withStyles from '@material-ui/core/styles/withStyles';
import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody.jsx';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import dashboardStyle from 'assets/jss/views/dashboardStyle.jsx';
import openSocket from 'socket.io-client';
import { REDEEM_SESSION_CODE, GET_PATIENT_FOR_SESSION, SESSION_STATUS } from 'graphql/queries';
import { getApolloClient } from 'utils/apolloClientFactory';
// import { checkCurrentUserPermission } from 'utils/permissionChecker';
import { withTranslation } from 'react-i18next';
import { cvGreenColor } from '../../styles/variables';

let bAckPing = false;
let nCountPing = 0;
const nCountPingMax = 5;
const nPingTimeAck = '2000';
const nPingTimeCheck = '5000';
let nIntervalID = -1;
let nPingAckTimeoutID = -1;
let bFirstConnection = true;
const styles = () => ({
  ...dashboardStyle,
  avatar: {
    backgroundColor: cvGreenColor,
    borderRadius: '0%',
  },
});

class Patient extends React.Component {
  constructor(props) {
    super(props);
    this.id = Date.now();
    window.comSocket = openSocket(`${window.ACTIVITY_SERVER_URL}/communication`, { autoConnect: false });
    window.activitySocket = openSocket(`${window.ACTIVITY_SERVER_URL}/activity`, { autoConnect: false });
    this.state = {
      communicationChannelJoined: false,
      communicationChannel: '',
      code: '',
    };
  }

  componentDidMount() {
    // Reconnexion automatique (cf. Docs/15) : si un token de session est present
    // ET toujours valide (seance non close cote therapeute, non expiree), on
    // rejoint sans faire ressaisir le code au patient (public age). Sinon on
    // nettoie le stockage et on affiche la saisie du code.
    const token = localStorage.getItem('sessionToken');
    const storedCode = localStorage.getItem('sessionCode');
    if (token && storedCode) {
      getApolloClient(false).then((client) => {
        client
          .query({ query: SESSION_STATUS, fetchPolicy: 'no-cache' })
          .then((r) => {
            if (r && r.data && r.data.sessionStatus) {
              this.setState({ code: storedCode }, () => this.handleJoin());
            } else {
              localStorage.removeItem('sessionToken');
              localStorage.removeItem('sessionCode');
            }
          })
          .catch(() => {});
      });
    }
  }

  componentWillUnmount() {
    // Même fuite que Header (Docs/17 dette) : checkConnectionStatusRoutine arme un setInterval
    // (nIntervalID, module) + un setTimeout interne, qui appellent setState (état de connexion
    // propre à cette vue). Sans nettoyage ils tournent APRÈS démontage → setState sur composant
    // démonté + pings socket fantômes. On annule UNIQUEMENT les deux minuteurs — surtout PAS les
    // sockets ni le handler CustomPing : window.comSocket/activitySocket sont créés ici mais
    // réutilisés par la vue activité, et la réponse au ping du thérapeute doit survivre au passage
    // à l'activité (sinon le thérapeute verrait le patient déconnecté pendant les activités).
    if (nIntervalID > -1) {
      clearInterval(nIntervalID);
      nIntervalID = -1;
    }
    if (nPingAckTimeoutID > -1) {
      clearTimeout(nPingAckTimeoutID);
      nPingAckTimeoutID = -1;
    }
  }

  setFullScreen = () => {
    if (process.env.REACT_APP_NODE_ENV !== 'development') {
      const docElm = document.documentElement;
      if (docElm.requestFullscreen) {
        docElm.requestFullscreen();
      } else if (docElm.mozRequestFullScreen) {
        docElm.mozRequestFullScreen();
      } else if (docElm.webkitRequestFullScreen) {
        docElm.webkitRequestFullScreen();
      } else if (docElm.msRequestFullscreen) {
        docElm.msRequestFullscreen();
      }
    };
  }

  checkConnectionStatusRoutine = (socket, roomID) => {
    if (nIntervalID > -1) {
      clearInterval(nIntervalID);
      }
      socket.emit('CustomPing', roomID);
      nIntervalID = setInterval(() => {
        bAckPing = false;

        socket.emit('CustomPing', roomID);
        nPingAckTimeoutID = setTimeout(() => {
          // console.log(bAckPing);
          if (bAckPing) {
            nCountPing = 0;
            // console.log(`${this.id}: Check Timeout Pong from Doctor OK`);
            this.setState({
              communicationChannelJoined: true,
            });
          } else if (nCountPing > nCountPingMax) {
            console.log(`${this.id}: Error Max Retry Pong`);
            this.handleDoctorDisconnected();
          } else {
            nCountPing += 1;
            // console.log(`${this.id}: Retry getting Pong from Doctor`);
          }
        }, nPingTimeAck);
      }, nPingTimeCheck);
    };

  handleJoin = () => {
    // Le code de seance est purement numerique (cf. utils/sessionCode.js). On ne
    // garde que les chiffres de la saisie (espaces, tirets... eventuels ignores)
    // pour que la comparaison exacte cote serveur (findOne({ code })) reussisse.
    const code = (this.state.code || '').replace(/\D/g, '');
    getApolloClient(false).then((client) => {
      // Chantier token de session (cf. Docs/15) : le poste patient echange
      // d'abord son code contre un token, PUIS lit son _id via un endpoint
      // token-gate a champs reduits (getPatientForSession). Plus de dossier
      // patient complet via getPatientByCode public.
      client
        .mutate({ mutation: REDEEM_SESSION_CODE, variables: { code } })
        .then((r) => {
          const auth = r && r.data && r.data.redeemSessionCode;
          if (!auth || !auth.token) throw new Error('Code de seance invalide');
          localStorage.setItem('sessionToken', auth.token);
          localStorage.setItem('sessionCode', code); // pour la reconnexion auto au reload
          // Le client porte desormais le token (x-session-token, cf.
          // apolloClientFactory) -> getPatientForSession derive le patient du token.
          return client.query({ query: GET_PATIENT_FOR_SESSION, fetchPolicy: 'no-cache' });
        })
        .then((response) => {
          // T4 : reponse sans patient (session invalide/expiree, endpoint refuse) → ne pas
          // crasher sur null._id (APP-COGNITION-1H8, ancien getPatientByCode) ; on jette une
          // erreur propre captee par le .catch (retour a la saisie du code).
          const patient = response && response.data && response.data.getPatientForSession;
          if (!patient || !patient._id) throw new Error('Session invalide');
          this.setState({
            communicationChannel: patient._id,
          });

            if (this.state.communicationChannel) {
              const sessionID = code;
              if (sessionID) {
                // Phase D (cf. Docs/15) : le handshake porte desormais le token de
                // session. L'activity-server (io.use) le verifie et le lie a la
                // room (token.code === sessionID) -> plus de connexion anonyme a
                // la seance. Forme fonction : le token frais est relu a chaque
                // (re)connexion. Le sessionID reste le code (= room, inchange).
                const patientAuth = (cb) =>
                  cb({ sessionID: `${code}`, token: localStorage.getItem('sessionToken') || undefined });
                window.comSocket.auth = patientAuth;
                sessionStorage.setItem('sessionID', sessionID);
                window.comSocket.connect();

                window.activitySocket.auth = patientAuth;
                window.activitySocket.connect();
              } else {
                throw new Error('missing code');
              }

              if (!window.comSocket) {
                throw new Error('missing window.comSocket');
              }
              window.comSocket.on('userDisconnected', () => {
                this.handleDoctorDisconnected();
              });

              window.comSocket.on('Ejected', () => {
                this.handleDoctorDisconnected();
              });

              //  window.comSocket.emit('CustomPing', sessionID);

              this.checkConnectionStatusRoutine(window.comSocket, sessionID);
              window.comSocket.removeAllListeners('CustomPing');
              window.comSocket.on('CustomPing', () => {
                // console.log(`${this.id} : CustomPing`);
                window.comSocket.emit('CustomPong', sessionID);
              });

              window.comSocket.removeAllListeners('CustomPong');
              window.comSocket.on('CustomPong', () => {
                // console.log(`${this.id} : Get Pong from Doctor`);

                if (bFirstConnection) {
                  // console.log('Get First Pong from Doctor');
                  this.setState({
                    communicationChannelJoined: true,
                  });
                  bFirstConnection = false;
                }
                bAckPing = true;
              });

              this.setState({
                communicationChannelJoined: false,
              });
              // window.comSocket.emit('getRoomSize', sessionID, (answer) => {
              //   if (answer.roomSize === 2) {
              //     this.setState({
              //       communicationChannelJoined: true,
              //     });
              //   } else {
              //     this.setState({
              //       communicationChannelJoined: false,
              //     });
              //   }
              // });

              // window.activitySocket.emit('getRoomSize', sessionID, (answer) => {
              //   if (answer.roomSize === 2) {
              //     this.setState({
              //       activityChannelJoined: true,
              //     });
              //   } else {
              //     this.setState({
              //       activityChannelJoined: false,
              //     });
              //   }
              // });
            }
          })
          .catch((err) => {
            // Code invalide ou seance close cote therapeute : on reste sur la
            // saisie du code (pas de reconnexion).
            console.log('Echec de connexion a la seance', err);
          });
      });
    };

  handleDoctorDisconnected = () => {
    console.log('doctor has left!');

    // Le therapeute a quitte : le token n'a plus lieu d'etre, on nettoie pour
    // ne pas tenter une reconnexion auto vers une seance close.
    localStorage.removeItem('sessionToken');
    localStorage.removeItem('sessionCode');

    window.comSocket.disconnect();
    window.activitySocket.disconnect();
    window.location.href = window.config.services.front.patient.site.url;
  };
  handleChange = (event) => {
    this.setState({
      code: event.target.value,
    });
  };

  keyPress = (event) => {
    event.persist();
    if (event.keyCode === 13) {
      this.handleJoin();
    }
  };

  render() {
    const { classes, t } = this.props;
    let core;

      if (!(this.state.communicationChannelJoined /* && this.state.activityChannelJoined */)) {
        core = (
          <Card className={classes.cardOuter}>
            <CardBody>
              <TextField
                autoComplete="off"
                id="activity"
                label={t('Main_SessionCodeInvite')}
                className={classes.textField}
                value={this.state.code}
                onChange={this.handleChange}
                onKeyDown={this.keyPress}
                margin="normal"
                variant="outlined"
                autoFocus
                // Code a 6 chiffres : clavier numerique (tablette) + longueur
                // bornee, pour des patients ages.
                inputProps={{ inputMode: 'numeric', maxLength: 6 }}
              />
              <Button style={{ display: 'block' }} variant="outlined" color="primary" onClick={this.handleJoin}>
                {t('Main_SessionJoin')}
              </Button>
            </CardBody>
          </Card>
        );
      } else {
        // removing permissionCheck for not connected users
        // if (!checkCurrentUserPermission('feature_disabledFullScreen'))
        // full screen does not work if not immediately following user input
        // this.setFullScreen();
        core = (
          <Redirect
            to={{
              pathname: `${window.config.services.front.patient.site.activityRoute}`,
              state: this.state,
              socket: window.comSocket,
            }}
          />
        );
      }

    return <div>{core}</div>;
  }
}

Patient.propTypes = {
  t: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
};

export default withTranslation()(withStyles(styles)(withRouter(Patient)));
